import React from "react";

// FauxArt v2 — procedural Terror TCG card faces. No assets, no network.
// Deterministic per card.id via mulberry32; unique SVG filter ids via useId.
// Props: { card, mode: "tile"|"full", silhouette, color, species }

function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function hashStr(s) {
  let h = 2166136261;
  const str = String(s == null ? "faux" : s);
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

const OBSIDIAN = "#0d0a16", OBSIDIAN2 = "#151021";
const PARCHMENT = "#e9ddc1", PARCH_DK = "#c9b689";
const GOLD = "#c9a84c", GOLD_LT = "#e8cf8a", GOLD_DK = "#6e5620";
const INK = "#2a1f14", NEARBLACK = "#0a0812";
const SERIF = "Luminari, 'Blackadder ITC', Georgia, serif";

// ───────────────────────── CLASS SCENES ─────────────────────────
// Each scene paints a 0..100 square (sliced into the portrait).
// e = energy 0..1 (level staging), R = seeded rng, c = class color, id = uid.
function stars(R, n, yMax, c, op) {
  const out = [];
  for (let i = 0; i < n; i++) out.push(<circle key={"s" + i} cx={R() * 100} cy={R() * yMax} r={0.35 + R() * 0.7} fill={i % 4 ? "#e8e4ff" : c} opacity={op * (0.4 + R() * 0.6)} />);
  return out;
}
const SCENES = {
  Solaea(R, e, c, id) {
    const rays = [];
    for (let i = 0; i < 24; i++) {
      const a = (i / 24) * Math.PI * 2;
      rays.push(<path key={i} d={`M50 34 L${50 + Math.cos(a) * (34 + e * 26)} ${34 + Math.sin(a) * (34 + e * 26)}`} stroke="#ffd98a" strokeWidth={i % 2 ? 0.7 : 2.2} opacity={0.25 + e * 0.35} />);
    }
    return (<g>
      <rect width="100" height="100" fill={`url(#sk${id})`} />
      <linearGradient id={`sk${id}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3d1f0a" /><stop offset="55%" stopColor="#8a4a14" /><stop offset="78%" stopColor="#d98a2b" /><stop offset="100%" stopColor="#5a2708" /></linearGradient>
      {rays}
      <circle cx="50" cy="34" r={15 + e * 8} fill="#ffedb8" /><circle cx="50" cy="34" r={11 + e * 6} fill="#fff8e0" />
      <path d="M0 78 Q25 71 50 74 Q75 77 100 72 L100 100 L0 100 Z" fill="#2e1305" />
      <path d="M0 85 Q30 80 60 84 Q85 87 100 83 L100 100 L0 100 Z" fill="#1c0b02" />
      <ellipse cx="50" cy="76" rx="34" ry="3" fill="#ffcf70" opacity={0.3 + e * 0.3} />
    </g>);
  },
  Lunaea(R, e, c, id) {
    const tides = [];
    for (let i = 0; i < 5; i++) tides.push(<path key={i} d={`M-5 ${62 + i * 9} Q25 ${58 + i * 9} 50 ${62 + i * 9} T105 ${62 + i * 9} L105 105 L-5 105 Z`} fill={i % 2 ? "#101a38" : "#182550"} opacity={0.85} />);
    return (<g>
      <rect width="100" height="100" fill={`url(#sk${id})`} />
      <linearGradient id={`sk${id}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#060a1e" /><stop offset="70%" stopColor="#14204a" /><stop offset="100%" stopColor="#0a1230" /></linearGradient>
      {stars(R, 26, 55, "#bcd2ff", 0.8)}
      <circle cx="66" cy="26" r={13 + e * 5} fill="#e8ecff" opacity={0.5 + e * 0.5} />
      <circle cx="61" cy="23" r={11 + e * 4.5} fill="#0a1030" />
      <circle cx="66" cy="26" r={17 + e * 7} fill="none" stroke="#aebfff" strokeWidth="0.6" opacity={0.3 + e * 0.4} />
      {tides}
      {Array.from({ length: 4 }, (_, i) => <path key={"c" + i} d={`M${8 + i * 24 + R() * 8} ${64 + i * 9} q6 ${-2.5} 12 0`} stroke="#9fb8e8" strokeWidth="0.7" fill="none" opacity={0.55} />)}
      {Array.from({ length: 7 }, (_, i) => <circle key={"g" + i} cx={58 + R() * 18} cy={64 + R() * 30} r={0.5 + R() * 0.7} fill="#dfe8ff" opacity={0.35 + R() * 0.4} />)}
    </g>);
  },
  Astrae(R, e, c, id) {
    const cons = []; let px = 15 + R() * 20, py = 15 + R() * 30;
    for (let i = 0; i < 6; i++) { const nx = px + 8 + R() * 14, ny = py + (R() - 0.5) * 22; cons.push(<line key={i} x1={px} y1={py} x2={nx} y2={ny} stroke="#9fb8e8" strokeWidth="0.35" opacity="0.7" />, <circle key={"c" + i} cx={nx} cy={ny} r="0.9" fill="#dfe8ff" />); px = nx; py = ny; }
    return (<g>
      <rect width="100" height="100" fill={`url(#sk${id})`} />
      <radialGradient id={`sk${id}`} cx="50%" cy="45%" r="75%"><stop offset="0%" stopColor="#1b2a55" /><stop offset="60%" stopColor="#0c1330" /><stop offset="100%" stopColor="#04060f" /></radialGradient>
      {stars(R, 40, 100, c, 0.9)}
      <ellipse cx="50" cy="50" rx={40} ry={13 + e * 4} fill="none" stroke={c} strokeWidth="0.7" opacity={0.5 + e * 0.3} transform="rotate(-16 50 50)" />
      <ellipse cx="50" cy="50" rx={30} ry={9} fill="none" stroke="#c9d6ff" strokeWidth="0.4" opacity="0.45" transform="rotate(-16 50 50)" />
      <ellipse cx="50" cy="50" rx={48} ry={17 + e * 5} fill="none" stroke={c} strokeWidth="0.4" opacity="0.35" transform="rotate(-16 50 50)" />
      <circle cx={50 + 30 * Math.cos(R() * 6)} cy={50 + 9 * Math.sin(R() * 6)} r="1.6" fill="#ffe9a8" />
      {cons}
    </g>);
  },
  "N'ihil"(R, e, c, id) {
    const flecks = [];
    for (let i = 0; i < 34; i++) { const edge = R() < 0.5; const x = edge ? (R() < 0.5 ? R() * 22 : 78 + R() * 22) : R() * 100; const y = edge ? R() * 100 : (R() < 0.5 ? R() * 20 : 80 + R() * 20); flecks.push(<rect key={i} x={x} y={y} width={0.8 + R() * 2} height={0.8 + R() * 2} fill={R() < 0.6 ? "#d8d4e2" : "#8a8598"} opacity={0.25 + R() * 0.5} transform={`rotate(${R() * 90} ${x} ${y})`} />); }
    return (<g>
      <rect width="100" height="100" fill={`url(#sk${id})`} />
      <radialGradient id={`sk${id}`} cx="50%" cy="46%" r="72%"><stop offset="0%" stopColor="#efedf4" /><stop offset={`${45 + e * 15}%`} stopColor="#b9b4c6" /><stop offset="100%" stopColor="#4e4960" /></radialGradient>
      <circle cx="50" cy="46" r={26 + e * 8} fill="#fbfaff" opacity="0.7" />
      {flecks}
      <path d="M0 0 L100 0 L100 12 Q50 6 0 14 Z" fill="#3a3550" opacity="0.5" />
      <path d="M0 100 L100 100 L100 90 Q50 96 0 88 Z" fill="#3a3550" opacity="0.5" />
    </g>);
  },
  "O'mnia"(R, e, c, id) {
    const licks = [];
    for (let i = 0; i < 9; i++) { const x = 4 + i * 11 + R() * 5, h = 18 + R() * 26 + e * 18; licks.push(<path key={i} d={`M${x} 100 C${x - 4} ${100 - h * 0.5} ${x + 5} ${100 - h * 0.7} ${x + 1} ${100 - h} C${x + 4} ${100 - h * 0.55} ${x + 7} ${100 - h * 0.35} ${x + 6} 100 Z`} fill={i % 2 ? "#e8542a" : "#8f1608"} opacity={0.65 + R() * 0.3} />); }
    return (<g>
      <rect width="100" height="100" fill={`url(#sk${id})`} />
      <linearGradient id={`sk${id}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#170305" /><stop offset="60%" stopColor="#3c0a08" /><stop offset="100%" stopColor="#701d0a" /></linearGradient>
      {stars(R, 12, 40, "#ff9a5a", 0.5)}
      <ellipse cx="50" cy="100" rx="60" ry={26 + e * 12} fill="#a83610" opacity="0.55" />
      {licks}
      {Array.from({ length: 10 }, (_, i) => <circle key={"e" + i} cx={R() * 100} cy={30 + R() * 55} r={0.5 + R() * 0.9} fill="#ffb35e" opacity={0.5 + R() * 0.5} />)}
    </g>);
  },
  Avarice(R, e, c, id) {
    const glints = [];
    for (let i = 0; i < 30; i++) { const x = R() * 100, y = 66 + R() * 30 - Math.abs(x - 50) * 0.22; glints.push(<circle key={i} cx={x} cy={y} r={0.7 + R() * 1.3} fill={R() < 0.75 ? "#e8b93e" : "#fff2c0"} opacity={0.6 + R() * 0.4} />); }
    return (<g>
      <rect width="100" height="100" fill={`url(#sk${id})`} />
      <radialGradient id={`sk${id}`} cx="50%" cy="80%" r="85%"><stop offset="0%" stopColor="#4a3208" /><stop offset="45%" stopColor="#211505" /><stop offset="100%" stopColor="#070502" /></radialGradient>
      <path d="M0 84 Q22 66 50 70 Q80 64 100 82 L100 100 L0 100 Z" fill="#5e4410" />
      <path d="M0 90 Q30 76 55 80 Q82 76 100 90 L100 100 L0 100 Z" fill="#8a6318" opacity="0.9" />
      {glints}
      <path d={`M50 ${58 - e * 8} L52 ${64 - e * 8} L58 ${64 - e * 8} L53 ${68 - e * 8} L55 ${74 - e * 8} L50 ${70 - e * 8} L45 ${74 - e * 8} L47 ${68 - e * 8} L42 ${64 - e * 8} L48 ${64 - e * 8} Z`} fill="#ffe9a0" opacity={0.5 + e * 0.5} />
    </g>);
  },
  "Dark Art"(R, e, c, id) {
    const spores = [];
    for (let i = 0; i < 22; i++) spores.push(<circle key={i} cx={R() * 100} cy={R() * 100} r={0.5 + R() * 1.1} fill={R() < 0.5 ? "#9fe86a" : "#c07ae8"} opacity={0.35 + R() * 0.45} />);
    return (<g>
      <rect width="100" height="100" fill={`url(#sk${id})`} />
      <linearGradient id={`sk${id}`} x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#121a08" /><stop offset="50%" stopColor="#25321000" /><stop offset="100%" stopColor="#1c0a26" /></linearGradient>
      <rect width="100" height="100" fill="#141c0a" />
      <ellipse cx="26" cy="72" rx="36" ry="24" fill="#2c4410" opacity="0.4" />
      <ellipse cx="76" cy="26" rx="36" ry="26" fill="#3a1448" opacity="0.45" />
      <ellipse cx="60" cy="80" rx="42" ry="17" fill="#1a2c08" opacity="0.6" />
      <path d="M10 100 C15 80 11 64 20 52 M30 100 C28 82 35 72 32 56 M72 100 C75 84 68 72 75 58 M90 100 C92 82 86 74 90 60" stroke="#0a1004" strokeWidth="1.4" fill="none" opacity="0.85" />
      <path d="M20 52 q-2 8 1 14 M32 56 q3 7 0 13 M75 58 q-3 8 0 14" stroke="#16240a" strokeWidth="0.7" fill="none" opacity="0.8" />
      {spores}
      <ellipse cx="50" cy="46" rx={30} ry={20} fill="#8fd85a" opacity={0.1 + e * 0.14} />
    </g>);
  },
  "Arcane Art"(R, e, c, id) {
    const ticks = [];
    for (let i = 0; i < 28; i++) { const a = (i / 28) * Math.PI * 2; ticks.push(<line key={i} x1={50 + Math.cos(a) * 33} y1={46 + Math.sin(a) * 33} x2={50 + Math.cos(a) * (i % 3 ? 36 : 39)} y2={46 + Math.sin(a) * (i % 3 ? 36 : 39)} stroke={c} strokeWidth="0.6" opacity="0.75" />); }
    return (<g>
      <rect width="100" height="100" fill={`url(#sk${id})`} />
      <radialGradient id={`sk${id}`} cx="50%" cy="46%" r="75%"><stop offset="0%" stopColor="#251a4a" /><stop offset="60%" stopColor="#120c28" /><stop offset="100%" stopColor="#070510" /></radialGradient>
      {stars(R, 14, 100, c, 0.5)}
      {[42, 33, 24].map((r, i) => <circle key={i} cx="50" cy="46" r={r} fill="none" stroke={c} strokeWidth={i === 1 ? 1.1 : 0.5} opacity={0.4 + e * 0.35} />)}
      {ticks}
      <path d="M50 22 L71 46 L50 70 L29 46 Z" fill="none" stroke={GOLD_LT} strokeWidth="0.7" opacity={0.5 + e * 0.4} />
      {Array.from({ length: 8 }, (_, i) => { const a = (i / 8) * Math.PI * 2 + 0.4; return <text key={"r" + i} x={50 + Math.cos(a) * 28.5} y={47.5 + Math.sin(a) * 28.5} fontSize="4" fill={c} opacity="0.8" textAnchor="middle" fontFamily="Georgia, serif">{"ϟΔΞΨΘΩΦλ"[i]}</text>; })}
    </g>);
  },
  "Shadow Gambling"(R, e, c, id) {
    const bits = [];
    for (let i = 0; i < 9; i++) {
      const x = 8 + R() * 84, y = 12 + R() * 76, s = 5 + R() * 5, rot = (R() - 0.5) * 50;
      if (R() < 0.5) bits.push(<g key={i} transform={`rotate(${rot} ${x} ${y})`} opacity={0.5 + R() * 0.4}><rect x={x - s / 2} y={y - s / 2} width={s} height={s} rx="1" fill="#17102b" stroke={c} strokeWidth="0.5" /><circle cx={x - s * 0.2} cy={y - s * 0.2} r="0.7" fill="#e8e2ff" /><circle cx={x + s * 0.2} cy={y + s * 0.2} r="0.7" fill="#e8e2ff" /></g>);
      else bits.push(<g key={i} transform={`rotate(${rot} ${x} ${y})`} opacity={0.45 + R() * 0.4}><rect x={x - s * 0.38} y={y - s * 0.55} width={s * 0.76} height={s * 1.1} rx="0.8" fill="#0f0a20" stroke={GOLD} strokeWidth="0.4" /><path d={`M${x} ${y - s * 0.22} L${x + s * 0.16} ${y} L${x} ${y + s * 0.22} L${x - s * 0.16} ${y} Z`} fill={c} /></g>);
    }
    return (<g>
      <rect width="100" height="100" fill={`url(#sk${id})`} />
      <radialGradient id={`sk${id}`} cx="50%" cy="40%" r="80%"><stop offset="0%" stopColor="#3a2358" /><stop offset="55%" stopColor="#1c1132" /><stop offset="100%" stopColor="#0a0616" /></radialGradient>
      <ellipse cx="50" cy="60" rx="45" ry="26" fill="#5e3a8a" opacity={0.2 + e * 0.15} />
      {bits}
      <ellipse cx="50" cy="88" rx="40" ry="8" fill="#2a1a45" opacity="0.8" />
    </g>);
  },
  Phantom(R, e, c, id) {
    const trees = [];
    for (let i = 0; i < 6; i++) { const x = 6 + i * 18 + R() * 6; trees.push(<path key={i} d={`M${x} 100 L${x - 1.6} 58 L${x - 7} 44 M${x - 1.6} 58 L${x + 4} 40 M${x - 1} 72 L${x + 6} 62`} stroke="#05040a" strokeWidth={2 + R()} fill="none" opacity="0.9" />); }
    const wisps = [];
    for (let i = 0; i < 5; i++) { const x = 12 + R() * 76, y = 34 + R() * 44; wisps.push(<path key={i} d={`M${x} ${y} C${x + 3} ${y - 7} ${x - 3} ${y - 12} ${x + 1} ${y - 18}`} stroke="#bfe6df" strokeWidth={1.4 + R()} strokeLinecap="round" fill="none" opacity={0.3 + R() * 0.35 + e * 0.15} />); }
    return (<g>
      <rect width="100" height="100" fill={`url(#sk${id})`} />
      <linearGradient id={`sk${id}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0e1c1e" /><stop offset="60%" stopColor="#16302c" /><stop offset="100%" stopColor="#060d0c" /></linearGradient>
      <ellipse cx="50" cy="44" rx="34" ry="24" fill="#68b8a8" opacity={0.12 + e * 0.15} />
      {trees}{wisps}
      <ellipse cx="50" cy="94" rx="52" ry="9" fill="#0a1512" />
    </g>);
  },
  Time(R, e, c, id) {
    const teeth = [];
    for (let i = 0; i < 16; i++) { const a = (i / 16) * Math.PI * 2; teeth.push(<rect key={i} x={-2} y={-40} width={4} height={6} fill="none" stroke="#c9b06a" strokeWidth="0.7" opacity="0.7" transform={`translate(50 48) rotate(${(a * 180) / Math.PI})`} />); }
    return (<g>
      <rect width="100" height="100" fill={`url(#sk${id})`} />
      <radialGradient id={`sk${id}`} cx="50%" cy="48%" r="75%"><stop offset="0%" stopColor="#2e2410" /><stop offset="60%" stopColor="#171106" /><stop offset="100%" stopColor="#080502" /></radialGradient>
      <circle cx="50" cy="48" r="37" fill="none" stroke="#c9b06a" strokeWidth="1.2" opacity={0.6 + e * 0.3} />
      {teeth}
      {Array.from({ length: 12 }, (_, i) => { const a = (i / 12) * Math.PI * 2; return <line key={"h" + i} x1={50 + Math.cos(a) * 30} y1={48 + Math.sin(a) * 30} x2={50 + Math.cos(a) * 33.5} y2={48 + Math.sin(a) * 33.5} stroke="#e8d9a0" strokeWidth="0.8" opacity="0.8" />; })}
      <path d="M14 82 A34 34 0 0 1 34 22" fill="none" stroke="#8a733a" strokeWidth="2.4" opacity="0.5" />
      <path d="M86 16 A44 44 0 0 1 90 66" fill="none" stroke="#8a733a" strokeWidth="1.6" opacity="0.4" />
      <line x1="50" y1="48" x2={50 + 14 * Math.cos(R() * 6)} y2={48 + 14 * Math.sin(R() * 6)} stroke="#e8d9a0" strokeWidth="1" opacity="0.75" />
      <line x1="50" y1="48" x2={50 + 22 * Math.cos(R() * 6)} y2={48 + 22 * Math.sin(R() * 6)} stroke="#e8d9a0" strokeWidth="0.6" opacity="0.75" />
    </g>);
  },
  Dragon(R, e, c, id) {
    const bolts = [];
    for (let i = 0; i < 2 + Math.round(e * 2); i++) { let x = 15 + R() * 70, y = 0, d = `M${x} 0`; while (y < 55 + R() * 25) { x += (R() - 0.5) * 16; y += 8 + R() * 10; d += ` L${x} ${y}`; } bolts.push(<path key={i} d={d} stroke="#eae6ff" strokeWidth={0.9 + R() * 0.8} fill="none" opacity={0.55 + e * 0.4} />); }
    return (<g>
      <rect width="100" height="100" fill={`url(#sk${id})`} />
      <linearGradient id={`sk${id}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1a1226" /><stop offset="55%" stopColor="#2c2138" /><stop offset="100%" stopColor="#120d1a" /></linearGradient>
      <ellipse cx="24" cy="20" rx="30" ry="13" fill="#0c0814" opacity="0.9" />
      <ellipse cx="72" cy="14" rx="34" ry="12" fill="#0e0a18" opacity="0.9" />
      <ellipse cx="50" cy="30" rx="45" ry="14" fill="#241a30" opacity="0.8" />
      {bolts}
      <path d="M0 88 L14 72 L26 84 L40 68 L54 82 L70 66 L84 80 L100 70 L100 100 L0 100 Z" fill="#0a0710" />
      <ellipse cx="50" cy="52" rx="34" ry="14" fill={c} opacity={0.08 + e * 0.12} />
    </g>);
  },
  Space(R, e, c, id) {
    const puffs = [];
    for (let i = 0; i < 10; i++) puffs.push(<ellipse key={i} cx={20 + R() * 60} cy={20 + R() * 60} rx={10 + R() * 18} ry={6 + R() * 12} fill={i % 3 === 0 ? "#8a3a9e" : i % 3 === 1 ? "#3a4a9e" : "#1a6a7e"} opacity={0.16 + R() * 0.2} transform={`rotate(${R() * 80} 50 50)`} />);
    return (<g>
      <rect width="100" height="100" fill={`url(#sk${id})`} />
      <radialGradient id={`sk${id}`} cx="45%" cy="45%" r="80%"><stop offset="0%" stopColor="#1c1030" /><stop offset="100%" stopColor="#030208" /></radialGradient>
      {puffs}
      {stars(R, 44, 100, "#ffe9c8", 0.95)}
      <ellipse cx="50" cy="50" rx={26 + e * 8} ry={10 + e * 4} fill="#c85ae8" opacity="0.18" transform="rotate(-24 50 50)" />
    </g>);
  },
};
SCENES.Beast = SCENES.Phantom; // safety alias for stray ilk-classed cards

function sceneOf(cls) { return SCENES[cls] || SCENES["Arcane Art"]; }

// ─────────────────── NON-BEING CENTERPIECES (0..100 box) ───────────────────
function Tome({ color }) {
  return (<g>
    <path d="M18 30 L50 22 L82 30 L82 78 L50 86 L18 78 Z" fill="#241a10" stroke={GOLD} strokeWidth="1.4" />
    <path d="M50 22 L50 86" stroke={GOLD} strokeWidth="1" opacity="0.8" />
    <path d="M22 34 L46 28 L46 80 L22 74 Z M78 34 L54 28 L54 80 L78 74 Z" fill="#3a2a16" stroke={GOLD_DK} strokeWidth="0.7" />
    <circle cx="50" cy="52" r="11" fill={NEARBLACK} stroke={color} strokeWidth="1.6" />
    <circle cx="50" cy="52" r="6" fill="none" stroke={color} strokeWidth="0.9" opacity="0.8" />
    <path d="M50 43 L52.5 49 L59 49 L54 53 L56 60 L50 55.5 L44 60 L46 53 L41 49 L47.5 49 Z" fill={color} opacity="0.9" />
    <path d="M18 30 L18 78 M82 30 L82 78" stroke={GOLD_LT} strokeWidth="0.6" opacity="0.7" />
    <path d="M26 24 L30 18 M74 24 L70 18" stroke={color} strokeWidth="1" opacity="0.7" />
  </g>);
}
function Impera({ color }) {
  const spokes = Array.from({ length: 12 }, (_, i) => { const a = (i / 12) * Math.PI * 2; return <path key={i} d={`M${50 + Math.cos(a) * 12} ${50 + Math.sin(a) * 12} L${50 + Math.cos(a) * (i % 2 ? 30 : 42)} ${50 + Math.sin(a) * (i % 2 ? 30 : 42)}`} stroke={color} strokeWidth={i % 2 ? 1 : 2.4} strokeLinecap="round" opacity="0.9" />; });
  return (<g>{spokes}
    <circle cx="50" cy="50" r="15" fill={NEARBLACK} stroke={color} strokeWidth="2" />
    <circle cx="50" cy="50" r="9.5" fill="none" stroke={GOLD} strokeWidth="0.9" />
    <path d="M50 42 L53 48 L59 48 L54.5 52 L56 58 L50 54.5 L44 58 L45.5 52 L41 48 L47 48 Z" fill={GOLD_LT} />
  </g>);
}
function Uhlti({ color, R }) {
  return (<g>
    <path d="M50 12 L64 30 L60 44 L66 58 L58 84 L42 84 L34 58 L40 44 L36 30 Z" fill="#1a1322" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M50 12 L54 30 L50 44 L54 58 L50 84" stroke={color} strokeWidth="0.6" opacity="0.5" fill="none" />
    <circle cx="50" cy="36" r="5.5" fill={NEARBLACK} stroke={GOLD} strokeWidth="1" />
    <circle cx="50" cy="36" r="2.2" fill={color} />
    <path d="M38 84 L62 84 L66 92 L34 92 Z" fill="#241a10" stroke={GOLD_DK} strokeWidth="1" />
    <path d="M42 62 L58 62 M44 70 L56 70" stroke={GOLD} strokeWidth="0.8" opacity="0.7" />
  </g>);
}
function Escling({ color }) {
  return (<g>
    <path d="M50 30 C38 32 32 42 32 54 C32 70 40 80 50 84 C60 80 68 70 68 54 C68 42 62 32 50 30 Z" fill="#100c1a" stroke={color} strokeWidth="1.4" />
    <path d="M50 30 C46 22 48 14 56 8 C54 16 58 20 62 22 C58 26 54 28 50 30 Z" fill={color} opacity="0.85" />
    <ellipse cx="44" cy="52" rx="2.6" ry="4" fill={color} opacity="0.95" />
    <ellipse cx="56" cy="52" rx="2.6" ry="4" fill={color} opacity="0.95" />
    <path d="M40 66 C44 70 56 70 60 66" stroke={color} strokeWidth="1" fill="none" opacity="0.6" />
    <path d="M32 54 C26 58 24 66 26 74 M68 54 C74 58 76 66 74 74" stroke={color} strokeWidth="1" fill="none" opacity="0.5" />
  </g>);
}

// ───────────────────────────── COMPONENT ─────────────────────────────
export default function FauxArt({ card = {}, mode = "full", silhouette = null, color = "#8a6cc9", species = null }) {
  const uid = React.useId().replace(/[^a-zA-Z0-9]/g, "");
  const seed = hashStr(card.id);
  const rng = mulberry32(seed);

  const level = card.level || (card.type === "Escling" ? 0 : 2);
  const e = level >= 3 ? 1 : level === 2 ? 0.55 : level === 1 ? 0.2 : 0.1;
  const cls = card.class || card.school || "Arcane Art";
  const type = card.type;
  const isBeing = card.lyf != null && card.lyt != null && type !== "Tome";

  // gems seated ON the inner frame line (perimeter of rect 6,6 → 94,144)
  const gems = [];
  {
    const gx0 = 6, gy0 = 6, gx1 = 94, gy1 = 144, W = gx1 - gx0, H = gy1 - gy0, P = 2 * (W + H);
    const n = 6 + Math.floor(rng() * 5);
    for (let i = 0; i < n; i++) {
      let t = ((i + 0.18 + rng() * 0.6) / n) * P, x, y;
      if (t < W) { x = gx0 + t; y = gy0; } else if (t < W + H) { x = gx1; y = gy0 + (t - W); }
      else if (t < 2 * W + H) { x = gx1 - (t - W - H); y = gy1; } else { x = gx0; y = gy1 - (t - 2 * W - H); }
      if (y > 2 && y < 17 && x > 30 && x < 70) continue; // keep banner row clean
      gems.push({ x, y, r: 0.8 + rng() * 0.6, hot: rng() > 0.6, dia: rng() > 0.55 });
    }
  }
  const nicks = Array.from({ length: 12 }, () => ({ x: 2 + rng() * 96, y: 2 + rng() * 146, l: 2 + rng() * 4, a: rng() * 180 }));
  const turbSeed = 1 + Math.floor(rng() * 900);
  const tilt = (rng() - 0.5) * 6, jx = (rng() - 0.5) * 4;

  const F = (n) => `${n}${uid}`;
  const defs = (
    <defs>
      <filter id={F("fg")} x="-10%" y="-10%" width="120%" height="120%">
        <feTurbulence type="fractalNoise" baseFrequency="0.55" numOctaves="3" seed={turbSeed} result="n" />
        <feColorMatrix in="n" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0.6 0.6 0.6 0 0" result="a" />
        <feComposite in="a" in2="SourceGraphic" operator="in" />
      </filter>
      <filter id={F("fb")} x="-20%" y="-20%" width="140%" height="140%">
        <feTurbulence type="fractalNoise" baseFrequency="0.9 0.6" numOctaves="3" seed={turbSeed} result="n" />
        <feDisplacementMap in="SourceGraphic" in2="n" scale="2" />
        <feComposite operator="in" in2="SourceGraphic" />
      </filter>
      <filter id={F("bl")} x="-150%" y="-150%" width="400%" height="400%"><feGaussianBlur stdDeviation="5" /></filter>
      <radialGradient id={F("gh")} cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor={color} stopOpacity={0.5 + e * 0.45} />
        <stop offset="45%" stopColor={color} stopOpacity={0.28 + e * 0.25} />
        <stop offset="100%" stopColor={color} stopOpacity="0" />
      </radialGradient>
      <radialGradient id={F("gv")} cx="50%" cy="45%" r="72%">
        <stop offset="58%" stopColor="#000" stopOpacity="0" /><stop offset="100%" stopColor="#000" stopOpacity="0.7" />
      </radialGradient>
      <linearGradient id={F("gb")} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#e3cd9d" /><stop offset="45%" stopColor="#c2a468" /><stop offset="100%" stopColor="#8a6a35" />
      </linearGradient>
      <linearGradient id={F("gp")} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#f2e8cd" /><stop offset="70%" stopColor={PARCHMENT} /><stop offset="100%" stopColor="#d6c69a" />
      </linearGradient>
    </defs>
  );

  // ── border frame ──
  const border = (
    <g>
      <rect x="0" y="0" width="100" height="150" fill={OBSIDIAN} />
      <g filter={`url(#${F("fb")})`}>
        <rect x="1" y="1" width="98" height="148" rx="3" fill={OBSIDIAN2} />
        <rect x="1.5" y="1.5" width="97" height="147" rx="3" fill="none" stroke={color} strokeOpacity="0.3" strokeWidth="1.6" />
        {nicks.map((k, i) => <line key={i} x1={k.x} y1={k.y} x2={k.x + k.l} y2={k.y} transform={`rotate(${k.a} ${k.x} ${k.y})`} stroke="#000" strokeOpacity="0.6" strokeWidth="0.5" />)}
      </g>
      <rect x="2.4" y="2.4" width="95.2" height="145.2" rx="2.6" fill="none" stroke={GOLD} strokeOpacity="0.5" strokeWidth="0.5" />
      <rect x="6" y="6" width="88" height="138" rx="2" fill="none" stroke={GOLD_DK} strokeWidth="1.1" />
      <rect x="6" y="6" width="88" height="138" rx="2" fill="none" stroke={GOLD} strokeWidth="0.45" strokeOpacity="0.9" />
      {gems.map((g, i) => (
        <g key={i}>
          <circle cx={g.x} cy={g.y} r={g.r + 0.7} fill={g.hot ? color : GOLD} opacity="0.25" />
          {g.dia
            ? <path d={`M${g.x} ${g.y - g.r} L${g.x + g.r} ${g.y} L${g.x} ${g.y + g.r} L${g.x - g.r} ${g.y} Z`} fill={g.hot ? color : GOLD} stroke="#000" strokeWidth="0.3" />
            : <circle cx={g.x} cy={g.y} r={g.r} fill={g.hot ? color : GOLD} stroke="#000" strokeWidth="0.3" />}
        </g>
      ))}
    </g>
  );

  // ── silhouette / centerpiece staging ──
  const sizeFrac = level >= 3 ? 0.85 : level === 2 ? 0.65 : 0.45;
  const R2 = mulberry32(seed ^ 0x5199);
  const figure = (bx, by, bs) => { // nested 100-box at (bx,by) size bs
    const glow = <ellipse cx="50" cy="34" rx="14" ry="11" fill={color} filter={`url(#${F("bl")})`} opacity={0.32 + e * 0.3} />;
    let body;
    if (isBeing && silhouette) {
      body = (
        <g transform={`rotate(${tilt} 50 55) translate(${jx} 0)`}>
          {glow}
          <path d={silhouette} fill={NEARBLACK} fillRule="evenodd" />
          <clipPath id={F("sc")}><path d={silhouette} fillRule="evenodd" /></clipPath>
          <g clipPath={`url(#${F("sc")})`}>
            <path d={silhouette} fill={color} fillRule="evenodd" opacity="0.22" transform="translate(-2.6 -2.2)" />
            <path d={silhouette} fill="#000" fillRule="evenodd" opacity="0.5" transform="translate(2.4 2.6)" />
          </g>
          <path d={silhouette} fill="none" fillRule="evenodd" stroke={color} strokeWidth="1.2" strokeLinejoin="round" opacity="0.95" />
        </g>
      );
    } else if (type === "Tome") body = <g>{glow}<Tome color={color} /></g>;
    else if (type === "Impera") body = <Impera color={color} />;
    else if (type === "U'hlti") body = <g>{glow}<Uhlti color={color} R={R2} /></g>;
    else if (type === "Escling") body = <Escling color={color} />;
    else body = <g>{glow}<Impera color={color} /></g>;
    return <svg x={bx} y={by} width={bs} height={bs} viewBox="0 0 100 100" overflow="visible">{body}</svg>;
  };

  // ── portrait ──
  const portrait = (x, y, w, h) => {
    const cx = x + w / 2;
    const s = h * sizeFrac;
    const by = level >= 3 ? y + h - s - h * 0.03 : level === 2 ? y + (h - s) * 0.62 : y + h - s - h * 0.06;
    const bx = cx - s / 2;
    const headY = by + s * 0.3;
    const crown = level >= 3 ? Array.from({ length: 9 }, (_, i) => { const a = Math.PI + (i / 8) * Math.PI; return <line key={i} x1={cx + Math.cos(a) * s * 0.3} y1={headY + Math.sin(a) * s * 0.26} x2={cx + Math.cos(a) * s * (i % 2 ? 0.42 : 0.52)} y2={headY + Math.sin(a) * s * (i % 2 ? 0.38 : 0.48)} stroke={GOLD_LT} strokeWidth={i % 2 ? 0.5 : 1} opacity="0.9" strokeLinecap="round" />; }) : null;
    return (
      <g>
        <clipPath id={F("pc")}><rect x={x} y={y} width={w} height={h} rx="1.5" /></clipPath>
        <g clipPath={`url(#${F("pc")})`}>
          <svg x={x} y={y} width={w} height={h} viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
            {sceneOf(cls)(mulberry32(seed ^ 0xa11ce), e, color, uid)}
          </svg>
          <ellipse cx={cx} cy={headY} rx={s * (0.5 + e * 0.28)} ry={s * (0.44 + e * 0.24)} fill={`url(#${F("gh")})`} />
          {crown}
          {figure(bx, by, s)}
          {level >= 3 ? <g opacity="0.85">
            <path d={`M${x + 1.5} ${y + 8} Q${x + 1.5} ${y + 1.5} ${x + 8} ${y + 1.5} M${x + w - 8} ${y + 1.5} Q${x + w - 1.5} ${y + 1.5} ${x + w - 1.5} ${y + 8}`} fill="none" stroke={GOLD_LT} strokeWidth="0.9" />
            <path d={`M${x + 1.5} ${y + h - 8} Q${x + 1.5} ${y + h - 1.5} ${x + 8} ${y + h - 1.5} M${x + w - 8} ${y + h - 1.5} Q${x + w - 1.5} ${y + h - 1.5} ${x + w - 1.5} ${y + h - 8}`} fill="none" stroke={GOLD_LT} strokeWidth="0.9" />
          </g> : null}
          <rect x={x} y={y} width={w} height={h} fill={`url(#${F("gv")})`} />
          <rect x={x} y={y} width={w} height={h} filter={`url(#${F("fg")})`} opacity="0.16" />
        </g>
        <rect x={x} y={y} width={w} height={h} rx="1.5" fill="none" stroke={GOLD_DK} strokeWidth="1.4" />
        <rect x={x} y={y} width={w} height={h} rx="1.5" fill="none" stroke={GOLD} strokeWidth="0.6" />
      </g>
    );
  };

  const shield = (cx, cy, s) => `M${cx} ${cy - s} L${cx + s * 0.85} ${cy - s * 0.5} L${cx + s * 0.8} ${cy + s * 0.35} L${cx} ${cy + s} L${cx - s * 0.8} ${cy + s * 0.35} L${cx - s * 0.85} ${cy - s * 0.5} Z`;
  const rootStyle = { position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden", borderRadius: "inherit", containerType: "size" };
  const speciesText = species ? `· ${String(species).toUpperCase()} ·` : (type && !isBeing ? `· ${type.toUpperCase()} ·` : null);

  // ================= TILE MODE =================
  if (mode === "tile") {
    return (
      <div style={rootStyle}>
        <svg viewBox="0 0 100 150" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" style={{ display: "block" }}>
          {defs}{border}
          {portrait(8, 21, 84, 105)}
          <rect x="8" y="4.5" width="84" height="10" rx="2" fill={`url(#${F("gb")})`} stroke={GOLD_DK} strokeWidth="0.8" />
          <rect x="8" y="15.5" width="84" height="5" fill="#100c1c" stroke={GOLD_DK} strokeWidth="0.4" />
          {level > 0 ? <g><circle cx="50" cy="132" r="5" fill="#3b2a12" stroke={GOLD} strokeWidth="0.8" /></g> : null}
        </svg>
        <div style={{ position: "absolute", top: "3.2%", left: "9%", right: "9%", height: "6.4%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: SERIF, fontWeight: "bold", fontStyle: "italic", color: INK, fontSize: 8, lineHeight: 1, whiteSpace: "nowrap", overflow: "hidden", textShadow: "0 1px 0 rgba(255,255,255,0.25)" }}>{card.name}</div>
        {speciesText ? <div style={{ position: "absolute", top: "10.5%", left: "8%", right: "8%", height: "3.2%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: SERIF, color: GOLD, fontSize: 5, letterSpacing: 1.4, whiteSpace: "nowrap", overflow: "hidden" }}>{speciesText}</div> : null}
        {level > 0 ? <div style={{ position: "absolute", top: "85%", left: "42%", width: "16%", height: "6%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: SERIF, fontWeight: "bold", color: PARCHMENT, fontSize: 8 }}>{level}</div> : null}
        {isBeing ? (
          <React.Fragment>
            <div style={tileStat("left", "#7a2733")}>{card.lyf}</div>
            <div style={tileStat("right", "#2c4a33")}>{card.lyt}</div>
          </React.Fragment>
        ) : card.khaoCost != null ? (
          <div style={{ position: "absolute", bottom: "2.5%", left: "4%", width: 17, height: 17, borderRadius: "50%", background: "#241a35", border: `1.5px solid ${GOLD}`, color: PARCHMENT, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: SERIF, fontWeight: "bold", fontSize: 9 }}>{card.khaoCost}</div>
        ) : null}
      </div>
    );
  }

  // ================= FULL MODE =================
  const bodyText = [];
  if (card.effect) bodyText.push({ t: card.effect, italic: false });
  if (card.flavorText) bodyText.push({ t: card.flavorText, italic: true });

  return (
    <div style={rootStyle}>
      <svg viewBox="0 0 100 150" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" style={{ display: "block" }}>
        {defs}{border}
        {portrait(13, 25, 74, 61)}
        {/* corner medallions */}
        <circle cx="9.5" cy="11" r="4.4" fill={OBSIDIAN2} stroke={GOLD} strokeWidth="0.7" />
        <circle cx="9.5" cy="11" r="2.5" fill={color} opacity="0.9" />
        <circle cx="90.5" cy="11" r="4.4" fill={OBSIDIAN2} stroke={GOLD} strokeWidth="0.7" />
        <circle cx="90.5" cy="11" r="2.5" fill={color} opacity="0.9" />
        {/* name banner */}
        <path d="M16 5.5 Q14 5 14 7.5 L14 11.5 Q14 14 16 13.5 L84 13.5 Q86 14 86 11.5 L86 7.5 Q86 5 84 5.5 Z" fill={`url(#${F("gb")})`} stroke={GOLD_DK} strokeWidth="0.8" />
        <path d="M14 7.5 Q11.5 8 12 9.5 Q11.5 11.5 14 11.5 M86 7.5 Q88.5 8 88 9.5 Q88.5 11.5 86 11.5" fill="none" stroke={GOLD_DK} strokeWidth="0.8" />
        {/* species strip under banner */}
        {speciesText ? <rect x="26" y="14.6" width="48" height="4.6" rx="1" fill="#100c1c" stroke={GOLD_DK} strokeWidth="0.45" /> : null}
        {/* level medallion — hangs below species strip, fully clear of the banner */}
        {level > 0 ? <g>
          <line x1="50" y1="13.5" x2="50" y2="17.5" stroke={GOLD_DK} strokeWidth="0.7" opacity="0.6" />
          <circle cx="50" cy="21.7" r="4" fill="#3b2a12" stroke={GOLD} strokeWidth="0.9" />
          <circle cx="50" cy="21.7" r="2.9" fill="none" stroke={GOLD_LT} strokeWidth="0.35" opacity="0.8" />
        </g> : null}
        {/* Experimental Edition bar inside portrait bottom */}
        <rect x="13.7" y="81.6" width="72.6" height="3.8" fill="#160f24" fillOpacity="0.88" stroke={GOLD_DK} strokeWidth="0.45" />
        {/* parchment text box, canon double rule */}
        <rect x="12" y="89.5" width="76" height="45.5" rx="2.5" fill={`url(#${F("gp")})`} stroke={GOLD_DK} strokeWidth="1.3" />
        <rect x="12" y="89.5" width="76" height="45.5" rx="2.5" fill="none" stroke={GOLD} strokeWidth="0.5" />
        <rect x="13.8" y="91.3" width="72.4" height="41.9" rx="1.8" fill="none" stroke={PARCH_DK} strokeWidth="0.7" />
        <rect x="14.6" y="92.1" width="70.8" height="40.3" rx="1.5" fill="none" stroke={PARCH_DK} strokeWidth="0.3" />
        {[[13.8, 91.3], [86.2, 91.3], [13.8, 133.2], [86.2, 133.2]].map(([qx, qy], i) => <circle key={i} cx={qx} cy={qy} r="1" fill={GOLD_DK} />)}
        {/* bottom shields / khao medallion */}
        {isBeing ? (
          <g>
            <path d={shield(11, 141.5, 5.6)} fill="#4a1d26" stroke={GOLD} strokeWidth="0.9" />
            <path d={shield(89, 141.5, 5.6)} fill="#1d3326" stroke={GOLD} strokeWidth="0.9" />
          </g>
        ) : card.khaoCost != null ? (
          <g>
            <circle cx="50" cy="141" r="6" fill="#241a35" stroke={GOLD} strokeWidth="0.9" />
            <circle cx="50" cy="141" r="4.4" fill="none" stroke={color} strokeWidth="0.5" opacity="0.85" />
          </g>
        ) : null}
      </svg>

      {/* DOM overlays */}
      <div style={ovl(4.6, 15, 70, 9.5)}>
        <span style={{ fontFamily: SERIF, fontWeight: "bold", fontStyle: "italic", color: INK, fontSize: "clamp(8px, 4.8cqw, 16px)", lineHeight: 1.02, textAlign: "center", textShadow: "0 1px 0 rgba(255,255,255,0.3)", overflow: "hidden", display: "block", width: "100%" }}>{card.name}</span>
      </div>
      {speciesText ? (
        <div style={ovl(14.4, 26, 48, 5)}>
          <span style={{ fontFamily: SERIF, color: GOLD, fontSize: "clamp(4px, 2cqw, 7px)", letterSpacing: "0.22em", whiteSpace: "nowrap" }}>{speciesText}</span>
        </div>
      ) : null}
      {level > 0 ? (
        <div style={ovl(19.2, 45, 10, 5.2)}>
          <span style={{ fontFamily: SERIF, fontWeight: "bold", color: GOLD_LT, fontSize: "clamp(7px, 3.2cqw, 11px)", textShadow: "0 1px 1px #000" }}>{level}</span>
        </div>
      ) : null}
      <div style={{ ...ovl(26.5, 14, 30, 4.4), justifyContent: "flex-start", paddingLeft: "1.5%" }}>
        <span style={smallLabel}>{card.ilk || ""}</span>
      </div>
      <div style={{ ...ovl(26.5, 56, 29, 4.4), justifyContent: "flex-end", paddingRight: "1.5%" }}>
        <span style={smallLabel}>{cls}</span>
      </div>
      <div style={{ ...ovl(81.4, 14.5, 40, 4), justifyContent: "flex-start" }}>
        <span style={{ fontFamily: SERIF, fontStyle: "italic", color: GOLD_LT, fontSize: "clamp(4px, 1.9cqw, 7px)", whiteSpace: "nowrap" }}>Experimental Edition</span>
      </div>
      <div style={{ ...ovl(81.4, 55, 31, 4), justifyContent: "flex-end" }}>
        <span style={{ fontFamily: SERIF, color: GOLD, fontSize: "clamp(3.5px, 1.7cqw, 6px)", whiteSpace: "nowrap", opacity: 0.8 }}>{String(card.id || "").slice(0, 10).toUpperCase()}</span>
      </div>
      <div style={{ position: "absolute", top: "61.8%", left: "17%", width: "66%", height: "26.5%", overflow: "hidden", fontFamily: "Georgia, serif", color: INK, fontSize: "clamp(6px, 2.9cqw, 10px)", lineHeight: 1.25 }}>
        {bodyText.map((b, i) => <p key={i} style={{ margin: i === 0 ? 0 : "0.5em 0 0", fontStyle: b.italic ? "italic" : "normal", fontWeight: b.italic ? "normal" : "bold" }}>{b.t}</p>)}
      </div>
      {isBeing ? (
        <React.Fragment>
          <div style={statLabel(1)}>Lyf</div>
          <div style={statLabel(89)}>Lyt</div>
          <div style={fullStat(6.7)}>{card.lyf}</div>
          <div style={fullStat(84.7)}>{card.lyt}</div>
        </React.Fragment>
      ) : card.khaoCost != null ? (
        <div style={fullStat(45.5)}>{card.khaoCost}</div>
      ) : null}
    </div>
  );
}

function ovl(topPct, leftPct, wPct, hPct) {
  return { position: "absolute", top: `${(topPct / 150) * 100}%`, left: `${leftPct}%`, width: `${wPct}%`, height: `${(hPct / 150) * 100}%`, display: "flex", alignItems: "center", justifyContent: "center" };
}
const smallLabel = { fontFamily: SERIF, fontWeight: "bold", color: "#f2ead6", fontSize: "clamp(5px, 2.5cqw, 8.5px)", textShadow: "0 1px 2px #000, 0 0 4px #000", whiteSpace: "nowrap" };
function statLabel(leftPct) {
  return { position: "absolute", top: "88.6%", left: `${leftPct}%`, width: "10%", height: "3%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: SERIF, fontWeight: "bold", color: PARCHMENT, fontSize: "clamp(4.5px, 2.1cqw, 7.5px)", textShadow: "0 1px 2px #000" };
}
function fullStat(leftPct) {
  return { position: "absolute", top: "91.6%", left: `${leftPct}%`, width: "9%", height: "5.4%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: SERIF, fontWeight: "bold", color: PARCHMENT, fontSize: "clamp(8px, 3.8cqw, 13px)", textShadow: "0 1px 2px #000" };
}
function tileStat(side, bg) {
  return { position: "absolute", bottom: "2%", [side]: "4%", width: 16, height: 16, background: bg, border: `1.5px solid ${GOLD}`, borderRadius: "30% 30% 50% 50%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: SERIF, fontWeight: "bold", color: PARCHMENT, fontSize: 9, textShadow: "0 1px 1px #000" };
}
